import type {ReactElement} from 'react';
import React from 'react';
import {isMobile as isMobileDevice} from 'react-device-detect';
import {ClickableDiv} from '@Components/clickable-div';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize, TextType} from '@Components/text';
import {MultiMediaUploadSource} from '@Libraries/user-media-library';
import type {ElementData} from '@Libraries/add-media-library';
import {openUserMediaModal} from '@Modals/user-media-modal';
import {UploadsMediaType} from '@Panels/user-media-panel/user-media-panel.types';
import type {StockModalProps} from '@Modals/stock-modal';
import {openStockMediaModal} from '@Modals/stock-modal';
import {StockPanelTabs} from '@Panels/stock-panel/stock-panel.types';
import {ControlledList} from '@Components/controlled-list';
import {CONTROLLED_LIST_ITEM_TYPE, type ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {AddItemThumbnail} from '@Components/poster-editor/components/poster-add-item-bar/components/add-item-thumbnail';
import type {StockSources} from '@Libraries/pmw-stock-media-library';
import styles from './media-image-panel-content.module.scss';

interface ImageTypeContentProps {
  isMobile?: boolean;
  panelRef: React.RefObject<HTMLDivElement | null>;
  panelId?: string;
  uploadSource?: MultiMediaUploadSource;
  maxSelectionNumber?: number;
  showVectors?: boolean;
  addInBulk?: boolean;
  hideRecordingOptions?: boolean;
  blacklistedSources?: StockSources[];
  exploreMediaStockTabs?: StockPanelTabs[];
  aiImagesPanelKey?: 'ai-images' | 'ai-stickers';
  /** Overrides what the Explore-media option opens (e.g. the stickers grid for sticker components) instead of the default stock panel. */
  onExploreMedia?: () => void;
  onAddSelected(items: ElementData[]): void;
  onAddBrandAsset?(): void;
  hideMyUploadsFooterSecondaryActions?: boolean;
  myDesignSelectedCallback?: VoidFunction;
  onStartAddSelected?: (numItems?: number) => void;
  onClose(): void;
}
export function MediaImagePanelContent({
  uploadSource = MultiMediaUploadSource.EMAIL_UPLOAD,
  hideRecordingOptions = false,
  showVectors = true,
  isMobile = false,
  onClose = (): void => {},
  ...props
}: ImageTypeContentProps): ReactElement<any> {
  const isAiStickersContext = props.aiImagesPanelKey === 'ai-stickers';

  const handleMyUploadsClick = (): void => {
    onClose();
    openUserMediaModal({
      hideRecordingOptions,
      onStartAddSelected: props.onStartAddSelected,
      onAddSelected: props.onAddSelected,
      onAddBrandAsset: props.onAddBrandAsset,
      defaultTab: UploadsMediaType.IMAGE,
      maxSelectionNumber: props.maxSelectionNumber,
      mediaTypesToShow: [UploadsMediaType.IMAGE],
      showVectors,
      uploadSource,
      addInBulk: props.addInBulk,
      uploadAcceptedMimetype: 'image/*',
      hideFooterSecondaryActions: props.hideMyUploadsFooterSecondaryActions,
    });
  };

  const buildStockImageModalProps = (tabsToShow: StockPanelTabs[], panelId: string, initialAiPanel?: StockModalProps['initialAiPanel']): StockModalProps => {
    return {
      onAddSelected: props.onAddSelected,
      onStartAddSelected: props.onStartAddSelected,
      tabsToShow,
      defaultSearchTab: tabsToShow[0],
      maxSelectionNumber: props.maxSelectionNumber,
      addInBulk: props.addInBulk,
      populateRecentlyAddedOnItemAdd: true,
      panelId,
      blacklistedSources: props.blacklistedSources,
      initialAiPanel,
    };
  };

  const handleExploreMediaClick = (): void => {
    onClose();
    if (props.onExploreMedia) {
      props.onExploreMedia();
      return;
    }
    const tabsToShow = props.exploreMediaStockTabs ?? [StockPanelTabs.PHOTOS, StockPanelTabs.TEXT_TO_IMAGE];
    openStockMediaModal(buildStockImageModalProps(tabsToShow, 'STOCK_PANEL_VIA_MEDIA_IMAGE'));
  };

  const handleAiImagesClick = (): void => {
    onClose();
    // Mobile devices open the self-hosted AI images panel (poster-editor parity); Back lands on the Photos grid. Desktop keeps the TEXT_TO_IMAGE tab.
    const aiImagesTabs: StockPanelTabs[] = isMobileDevice ? [StockPanelTabs.PHOTOS] : [StockPanelTabs.TEXT_TO_IMAGE];
    openStockMediaModal(buildStockImageModalProps(aiImagesTabs, 'STOCK_PANEL_VIA_AI_IMAGES', isMobileDevice ? (props.aiImagesPanelKey ?? 'ai-images') : undefined));
  };

  const handleMyDesignsClick = (): void => {
    onClose();
    if (props.myDesignSelectedCallback) {
      props.myDesignSelectedCallback();
    }
  };

  const getLargeScreenContentIcon = (icon: string): ReactElement<any> => {
    return (
      <Icon
        className={styles.mediaTypeIcon}
        size={IconSize.SIZE_ICON_24}
        icon={icon}
        shape={IconShape.CIRCLE}
        type={IconType.FLAT}
        flatIconType={{type: FlatIconColorType.DEFAULT, color: FlatIconColor.PRIMARY}}
      />
    );
  };

  const getLargeScreenContentLabel = (label: string): ReactElement<any> => {
    return <Text size={TextSize.XSMALL} type={TextType.BODY} className="spacing-m-t-2 text-center" val={label} />;
  };

  const getLargeScreenContent = (): ReactElement<any> => {
    return (
      <div ref={props.panelRef} className={`flex-center spacing-p-t-7 spacing-p-b-7 spacing-p-l-12 spacing-p-r-12 ${styles.content}`}>
        <ClickableDiv onClick={handleMyUploadsClick} className={`hover-transition-all flex-v-row flex-center border-s-dark radius-8 ${styles.option}`}>
          {getLargeScreenContentIcon('icon-upload')}
          {getLargeScreenContentLabel(window.i18next.t('pmwjs_myuploads'))}
        </ClickableDiv>
        <ClickableDiv onClick={handleExploreMediaClick} className={`hover-transition-all flex-v-row flex-center border-s-dark radius-8 ${styles.option}`}>
          {getLargeScreenContentIcon('icon-images')}
          {getLargeScreenContentLabel(window.i18next.t('pmwjs_explore_media'))}
        </ClickableDiv>
        <ClickableDiv onClick={handleAiImagesClick} className={`hover-transition-all flex-v-row flex-center border-s-dark radius-8 ${styles.option}`}>
          {getLargeScreenContentIcon(isAiStickersContext ? 'icon-ai-sticker' : 'icon-ai-image')}
          {getLargeScreenContentLabel(window.i18next.t(isAiStickersContext ? 'pmwjs_ai_stickers_caps' : 'pmwjs_ai_images'))}
        </ClickableDiv>
        {props.myDesignSelectedCallback ? (
          <ClickableDiv onClick={handleMyDesignsClick} className={`hover-transition-all flex-v-row flex-center border-s-dark radius-8 ${styles.option}`}>
            {getLargeScreenContentIcon('icon-designs')}
            {getLargeScreenContentLabel(window.i18next.t('pmwjs_my_designs'))}
          </ClickableDiv>
        ) : null}
      </div>
    );
  };

  const getSmallScreenContent = (): ReactElement<any> => {
    const items: ControlledListItemType[] = [
      {
        id: 'my-uploads',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        subText: '',
        className: 'spacing-m-b-3',
        text: window.i18next.t('pmwjs_myuploads'),
        onClick: (): void => {
          handleMyUploadsClick();
        },
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: <AddItemThumbnail icon="icon-upload" />,
        },
      },
      {
        id: 'explore-media',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        subText: '',
        className: 'spacing-m-b-3',
        text: window.i18next.t('pmwjs_explore_media'),
        onClick: (): void => {
          handleExploreMediaClick();
        },
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: <AddItemThumbnail icon="icon-images" />,
        },
      },
      {
        id: 'ai-images',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        subText: '',
        className: 'spacing-m-b-3',
        text: window.i18next.t(isAiStickersContext ? 'pmwjs_ai_stickers_caps' : 'pmwjs_ai_images'),
        onClick: (): void => {
          handleAiImagesClick();
        },
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: <AddItemThumbnail icon={isAiStickersContext ? 'icon-ai-sticker' : 'icon-ai-image'} />,
        },
      },
    ];

    if (props.myDesignSelectedCallback) {
      items.push({
        id: 'my-designs',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        subText: '',
        className: 'spacing-m-b-3',
        text: window.i18next.t('pmwjs_my_designs'),
        onClick: (): void => {
          handleMyDesignsClick();
        },
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: <AddItemThumbnail icon="icon-designs" />,
        },
      });
    }
    return <ControlledList className="spacing-p-b-2 spacing-p-t-1 spacing-p-r-4 spacing-p-l-4 spacing-m-0" items={items} />;
  };

  const getContent = (): ReactElement<any> => {
    if (isMobile) {
      return getSmallScreenContent();
    }
    return getLargeScreenContent();
  };

  return getContent();
}
